import { useState } from 'react';
import { Check, Sparkles, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { usePaddleCheckout } from '@/hooks/usePaddleCheckout';
import { useCreditPlans, useWallet } from '@/hooks/useWallet';
import { formatCredits } from '@/lib/api/wallet';
import { PurchaseConsentDialog } from './PurchaseConsentDialog';

/**
 * Unified subscription plans. One credit balance covers Writer turns, compiles
 * and AI reviews. The tiers are the same product with a bigger monthly
 * balance, so the cards say it cumulatively: Free lists everything, each paid
 * card says "Everything in X, plus" only what it actually adds. The only real
 * feature split is free vs paid: the PDF credit line and the LaTeX source
 * export — both enforced server-side (compiler footer, writer_export_source).
 */

const FREE_FEATURES = [
  'LaTeX editor with the writing agent',
  'AI reviews of your own drafts',
  'Reviews of public arXiv papers are free, always',
  'PDF download',
  'Credits reset each month; nothing carries over',
  'PDFs carry a small “Made with PaperVerse | paperverse.io” credit line on every page',
  'LaTeX source export is a paid-plan feature',
];

interface PaidTier {
  scale: string;
  adds: string[];
  popular?: boolean;
}

const PAID_TIERS: Record<string, PaidTier> = {
  researcher: {
    scale: 'Enough for one paper, written and checked as you go',
    adds: [
      'Clean PDFs — no PaperVerse credit line',
      'Export the full LaTeX source (main.tex + references.bib)',
      'Unused credits carry into the next month',
    ],
  },
  pro: {
    scale: 'Enough for a few manuscripts in parallel',
    adds: ['Three times the monthly credits'],
    popular: true,
  },
  lab: {
    scale: 'Enough for the monthly output of a research group',
    adds: ['Ten times the monthly credits'],
  },
};

export const SubscriptionPlans = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: plans = [], isLoading } = useCreditPlans();
  const { data: wallet } = useWallet();
  const { openCheckout } = usePaddleCheckout();
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [consentOpen, setConsentOpen] = useState(false);
  const [selectedPlanCode, setSelectedPlanCode] = useState<string | null>(null);

  const selectedPlan = plans.find((p) => p.planCode === selectedPlanCode) ?? null;

  // Section 312j(2)/(3) BGB: recurring price, billing interval and minimum term
  // must be shown immediately before the paid subscription is concluded.
  const handleSubscribe = (planCode: string) => {
    if (!user) {
      sessionStorage.setItem('redirectAfterAuth', '/pricing');
      navigate('/auth');
      return;
    }
    if (planCode === 'free') {
      navigate('/dashboard');
      return;
    }
    setSelectedPlanCode(planCode);
    setConsentOpen(true);
  };

  const startCheckout = async (planCode: string) => {
    setLoadingPlan(planCode);
    try {
      const { data, error } = await supabase.functions.invoke('create-paddle-checkout', {
        body: { planCode },
      });
      if (error) throw error;
      if (!data?.transactionId) throw new Error('No transaction ID received');
      setConsentOpen(false);
      openCheckout(data.transactionId);
    } catch (error) {
      console.error('Error creating subscription checkout:', error);
      toast.error('Unable to start checkout. Please try again.');
    } finally {
      setLoadingPlan(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (plans.length === 0) return null;

  const ordered = [...plans].sort((a, b) => a.priceEurMonthly - b.priceEurMonthly);

  return (
    <div className="w-full">
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 max-w-6xl mx-auto">
        {ordered.map((plan, index) => {
          const isFree = plan.planCode === 'free';
          const tier = PAID_TIERS[plan.planCode];
          const previousName = index > 0 ? ordered[index - 1].displayName : null;
          const isCurrent = wallet?.planCode === plan.planCode;
          return (
            <Card
              key={plan.planCode}
              className={`relative flex flex-col ${
                tier?.popular
                  ? 'border-primary shadow-lg ring-2 ring-primary/20'
                  : isFree
                    ? 'border-dashed border-muted-foreground/30 bg-muted/30'
                    : 'border-border'
              }`}
            >
              {tier?.popular && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground">
                  <Sparkles className="h-3 w-3 mr-1" />
                  POPULAR
                </Badge>
              )}

              <CardHeader className="text-center pb-4">
                <h3 className="text-xl font-semibold">{plan.displayName}</h3>
                <div className="mt-4">
                  <span className="text-4xl font-bold">€{plan.priceEurMonthly}</span>
                  <span className="text-sm text-muted-foreground ml-1">/month</span>
                </div>
                <p className="text-sm font-medium mt-3">
                  {formatCredits(plan.monthlyCredits)} credits / month
                </p>
                {tier && (
                  <p className="text-xs text-muted-foreground mt-1 leading-snug">
                    {tier.scale}
                  </p>
                )}
              </CardHeader>

              <CardContent className="flex-1">
                {!isFree && previousName && (
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-3">
                    Everything in {previousName}, plus:
                  </p>
                )}
                <ul className="space-y-2.5">
                  {(isFree ? FREE_FEATURES : (tier?.adds ?? [])).map((feature) => (
                    <li key={feature} className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>

              <CardFooter>
                <Button
                  className="w-full"
                  variant={tier?.popular ? 'default' : 'outline'}
                  onClick={() => handleSubscribe(plan.planCode)}
                  disabled={loadingPlan === plan.planCode || isCurrent}
                >
                  {loadingPlan === plan.planCode
                    ? 'Loading...'
                    : isCurrent
                      ? 'Current plan'
                      : isFree
                        ? (user ? 'Included' : 'Get started free')
                        : 'Subscribe'}
                </Button>
              </CardFooter>
            </Card>
          );
        })}
      </div>

      {selectedPlan && user && (
        <PurchaseConsentDialog
          open={consentOpen}
          onOpenChange={setConsentOpen}
          mode="subscription"
          tier={{
            name: `${selectedPlan.displayName} — €${selectedPlan.priceEurMonthly} / month`,
            price: `€${selectedPlan.priceEurMonthly} / month`,
            reviews: selectedPlan.monthlyCredits,
            packType: selectedPlan.planCode,
          }}
          userId={user.id}
          onConfirm={() => startCheckout(selectedPlan.planCode)}
          isLoading={loadingPlan === selectedPlan.planCode}
        />
      )}

      <p className="text-center text-xs text-muted-foreground mt-6 max-w-3xl mx-auto">
        Failed runs cost nothing · No surprise bills · Cancel any time · Secure
        checkout via Paddle
      </p>
    </div>
  );
};
